import type { CalculatorInputs, CalculatorQuestion, Estimate } from "./calculator";
import { number, usd } from "./calculator";

export type OrchestrationState = {
  summary: string;
  nextPrompt: string | null;
  modelStatus: "idle" | "loading" | "model_orchestrated" | "deterministic_fallback" | "rate_limited";
  pricingAuthority: "local_deterministic_tools";
};

export function fallbackOrchestration(
  inputs: CalculatorInputs,
  estimate: Estimate,
  currentQuestion: CalculatorQuestion | undefined
): OrchestrationState {
  void inputs;

  return {
    summary: estimate.complete
      ? `THE SHIFT recommends ${estimate.recommendedPlan}: ${estimate.agentMembers} agent members for ${number(estimate.tasksPerMonth)} monthly tasks at ${usd(estimate.costPerTask, 2)} per task.`
      : currentQuestion?.prompt ?? "Continue answering the required questions to complete the estimate.",
    nextPrompt: currentQuestion?.prompt ?? null,
    modelStatus: "deterministic_fallback",
    pricingAuthority: "local_deterministic_tools"
  };
}

export async function requestOrchestration(
  inputs: CalculatorInputs,
  estimate: Estimate,
  currentQuestion: CalculatorQuestion | undefined,
  signal?: AbortSignal
): Promise<OrchestrationState> {
  const fallback = fallbackOrchestration(inputs, estimate, currentQuestion);
  if (!shouldRequestRemoteOrchestration()) return fallback;

  try {
    const response = await fetch("/api/orchestrate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        inputs,
        currentQuestion: currentQuestion ? { id: currentQuestion.id, prompt: currentQuestion.prompt } : undefined,
        estimate: {
          complete: estimate.complete,
          recommendedPlan: estimate.recommendedPlan,
          monthlyEstimate: estimate.monthlyEstimate,
          costPerTask: estimate.costPerTask,
          humanEquivalentFte: estimate.humanEquivalentFte,
          agentMembers: estimate.agentMembers,
          tasksPerMonth: estimate.tasksPerMonth,
          automationPotential: estimate.automationPotential,
          annualSavings: estimate.annualSavings,
          workUnits: estimate.workUnits
        }
      }),
      signal
    });

    if (!response.ok) return fallback;

    const data = await response.json() as Partial<OrchestrationState>;
    return {
      summary: typeof data.summary === "string" ? data.summary : fallback.summary,
      nextPrompt: typeof data.nextPrompt === "string" || data.nextPrompt === null ? data.nextPrompt : fallback.nextPrompt,
      modelStatus:
        data.modelStatus === "model_orchestrated" || data.modelStatus === "rate_limited"
          ? data.modelStatus
          : "deterministic_fallback",
      pricingAuthority: "local_deterministic_tools"
    };
  } catch {
    return fallback;
  }
}

function shouldRequestRemoteOrchestration(): boolean {
  if (import.meta.env.VITE_ENABLE_ORCHESTRATION === "true") return true;
  if (typeof window === "undefined") return false;
  return !["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname);
}
